/**
 * CourtPage - Dedicated page for viewing court details and game schedule.
 *
 * Route: /admin/courts/:courtId
 */

import { useMemo } from 'react'; // Used for summary calculation
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Chip,
  CircularProgress,
  Alert,
  Button,
  Breadcrumbs,
  Link,
  LinearProgress,
} from '@mui/material';
import BackIcon from '@mui/icons-material/ArrowBack';
import { useQuery } from '@tanstack/react-query';

import { gamesApi } from '@/api/client';
import { EventLog } from '@/components/EventLog';
import { GamesListTable } from '@/components/GamesListTable';
import { formatTime } from '@/lib/i18n';
import type { Game } from '@/types';
import { getGameStatus } from '@/types';

export function CourtPage() {
  const { courtId } = useParams<{ courtId: string }>();
  const navigate = useNavigate();
  // Support both numeric and letter-based court IDs
  const courtIdValue = courtId ?? null;

  const handleGameClick = (gameId: number) => {
    navigate(`/admin/games/${gameId}`);
  };

  const handleTeamClick = (teamId: number) => {
    navigate(`/admin/teams/${teamId}`);
  };

  const handleBlockClick = (paperId: number) => {
    navigate(`/admin/blocks/${paperId}`);
  };

  // Parse court ID - support both numeric and letter-based IDs
  const courtIdNum = courtIdValue ? parseInt(courtIdValue, 10) : null;

  // Fetch games for this court using server-side filtering
  const {
    data: courtGames = [],
    isLoading,
    error,
  } = useQuery<Game[]>({
    queryKey: ['games', 'court', courtIdNum],
    queryFn: () => gamesApi.list({ court: courtIdNum! }),
    enabled: courtIdNum !== null && !isNaN(courtIdNum),
  });

  // Calculate progress and time range
  const summary = useMemo(() => {
    if (!courtGames.length)
      return { finished: 0, total: 0, inProgress: 0, firstStart: null, lastEnd: null };

    let finished = 0;
    let inProgress = 0;
    let firstStart: string | null = null;
    let lastEnd: string | null = null;

    for (const game of courtGames) {
      const status = getGameStatus(game);
      if (status === 'finished') finished++;
      else if (status === 'in_progress') inProgress++;

      if (!firstStart || game.schedule_start < firstStart) {
        firstStart = game.schedule_start;
      }
      if (!lastEnd || game.schedule_end > lastEnd) {
        lastEnd = game.schedule_end;
      }
    }

    return { finished, total: courtGames.length, inProgress, firstStart, lastEnd };
  }, [courtGames]);

  if (!courtIdValue) {
    return (
      <Box>
        <Alert severity="error">Invalid court ID</Alert>
        <Button startIcon={<BackIcon />} onClick={() => navigate('/admin/courts')} sx={{ mt: 2 }}>
          Back to Courts
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link component={RouterLink} to="/admin" underline="hover" color="inherit">
          Admin
        </Link>
        <Link component={RouterLink} to="/admin/courts" underline="hover" color="inherit">
          Courts
        </Link>
        <Typography color="text.primary">Court {courtIdValue}</Typography>
      </Breadcrumbs>

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Box>
          <Alert severity="error" sx={{ mb: 2 }}>
            Failed to load court details
          </Alert>
          <Button startIcon={<BackIcon />} onClick={() => navigate('/admin/courts')}>
            Back to Courts
          </Button>
        </Box>
      ) : (
        <>
          {/* Header */}
          <Typography variant="h4" fontWeight={700} gutterBottom>
            Court {courtIdValue}
          </Typography>

          {/* Summary Card */}
          <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Time: {summary.firstStart ? formatTime(summary.firstStart) : '-'} -{' '}
                {summary.lastEnd ? formatTime(summary.lastEnd) : '-'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                | Progress: {summary.finished}/{summary.total}
              </Typography>
              {summary.inProgress > 0 && (
                <Chip
                  label={`${summary.inProgress} in progress`}
                  size="small"
                  color="primary"
                />
              )}
            </Box>
            <LinearProgress
              variant="determinate"
              value={summary.total > 0 ? (summary.finished / summary.total) * 100 : 0}
              sx={{ height: 8, borderRadius: 4 }}
            />
          </Paper>

          {/* Games List */}
          <Typography variant="h5" fontWeight={600} gutterBottom>
            Games ({courtGames.length})
          </Typography>

          {courtGames.length > 0 ? (
            <Paper sx={{ height: 500, overflow: 'hidden' }}>
              <GamesListTable
                games={courtGames}
                columns={{ time: true, block: true, court: false, teams: true, score: true, status: true }}
                onGameClick={handleGameClick}
                onTeamClick={handleTeamClick}
                onBlockClick={handleBlockClick}
                variant="elevation"
                maxHeight={500}
              />
            </Paper>
          ) : (
            <Paper sx={{ p: 3, textAlign: 'center' }}>
              <Typography color="text.secondary">No games scheduled for this court</Typography>
            </Paper>
          )}

          {/* Event Log */}
          {courtIdNum && <EventLog params={{ court_id: courtIdNum }} title="Court Event Log" />}
        </>
      )}
    </Box>
  );
}

